<script setup>
import { ref, onMounted, computed } from "vue";
import subs from "@/api/iotSubscriptions";
import { useDialogStore } from "@/stores/dialog";
import { exportToCsv } from "@/utils/exportUtils";

const dialog = useDialogStore();

const items = ref([]);
const loading = ref(false);
const error = ref("");
const search = ref("");

const showForm = ref(false);
const editItem = ref(null);
const showApiKey = ref(false);
const saving = ref(false);

const SUBSCRIPTION_TYPES = ["PeopleCounter", "Weather", "AirQuality", "SatelliteImagery"];
const SOURCES_BY_TYPE = {
  PeopleCounter: ["ecocounter", "ecovisio", "telraam"],
  Weather: ["openmeteo"],
  AirQuality: [],
  SatelliteImagery: ["copernicus"],
};

function emptyForm() {
  return {
    subscriptionId: null,
    subscriptionType: "PeopleCounter",
    source: "ecocounter",
    externalId: "",
    apiKey: "",
    config: "",
    enabled: true,
    pollMinutes: null,
  };
}

const form = ref(emptyForm());

const availableSources = computed(() => SOURCES_BY_TYPE[form.value.subscriptionType] ?? []);

async function load() {
  loading.value = true;
  error.value = "";
  try {
    const raw = await subs.list();
    items.value = (raw || []).map(normalise);
  } catch (err) {
    error.value = err?.response?.data || err?.message || String(err);
  } finally {
    loading.value = false;
  }
}

function normalise(r) {
  return {
    subscriptionId: r.subscriptionId ?? r.SubscriptionId,
    customerId: r.customerId ?? r.CustomerId,
    subscriptionType: r.subscriptionType ?? r.SubscriptionType ?? "",
    source: r.source ?? r.Source ?? "",
    externalId: r.externalId ?? r.ExternalId ?? "",
    apiKey: r.apiKey ?? r.ApiKey ?? null,
    config: r.config ?? r.Config ?? null,
    enabled: r.enabled ?? r.Enabled ?? false,
    pollMinutes: r.pollMinutes ?? r.PollMinutes ?? null,
    lastSyncedUtc: r.lastSyncedUtc ?? r.LastSyncedUtc ?? null,
    lastErrorUtc: r.lastErrorUtc ?? r.LastErrorUtc ?? null,
    lastErrorMsg: r.lastErrorMsg ?? r.LastErrorMsg ?? null,
    createdUtc: r.createdUtc ?? r.CreatedUtc ?? null,
  };
}

const filtered = computed(() => {
  if (!search.value) return items.value;
  const q = search.value.toLowerCase();
  return items.value.filter(
    (s) =>
      (s.subscriptionType || "").toLowerCase().includes(q) ||
      (s.source || "").toLowerCase().includes(q) ||
      (s.externalId || "").toLowerCase().includes(q),
  );
});

const headers = [
  { title: "Type", key: "subscriptionType", sortable: true },
  { title: "Source", key: "source", sortable: true },
  { title: "External ID", key: "externalId", sortable: true },
  { title: "Auth", key: "auth", sortable: false },
  { title: "Enabled", key: "enabled", sortable: true },
  { title: "Poll (min)", key: "pollMinutes", sortable: true },
  { title: "Last Synced", key: "lastSyncedUtc", sortable: true },
  { title: "Status", key: "status", sortable: false },
  { title: "Actions", key: "actions", sortable: false, align: "end", width: "150" },
];

function fmtDate(v) {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleString("en-GB");
  } catch {
    return v;
  }
}

function openCreate() {
  editItem.value = null;
  form.value = emptyForm();
  showApiKey.value = false;
  showForm.value = true;
}

function openEdit(item) {
  editItem.value = item;
  form.value = {
    subscriptionId: item.subscriptionId,
    subscriptionType: item.subscriptionType || "PeopleCounter",
    source: item.source || "",
    externalId: item.externalId || "",
    apiKey: item.apiKey || "",
    config: item.config || "",
    enabled: !!item.enabled,
    pollMinutes: item.pollMinutes,
  };
  showApiKey.value = false;
  showForm.value = true;
}

async function save() {
  if (!form.value.subscriptionType) {
    error.value = "Subscription Type is required.";
    return;
  }
  if (!form.value.source) {
    error.value = "Source is required.";
    return;
  }
  if (!form.value.externalId) {
    error.value = "External ID is required.";
    return;
  }
  if (form.value.config) {
    try {
      JSON.parse(form.value.config);
    } catch (e) {
      error.value = `Config must be valid JSON: ${e.message}`;
      return;
    }
  }

  saving.value = true;
  error.value = "";
  try {
    const payload = {
      subscriptionType: form.value.subscriptionType,
      source: form.value.source,
      externalId: form.value.externalId,
      apiKey: form.value.apiKey || null,
      config: form.value.config || null,
      enabled: !!form.value.enabled,
      pollMinutes: form.value.pollMinutes ? Number(form.value.pollMinutes) : null,
    };
    if (form.value.subscriptionId) {
      await subs.update(form.value.subscriptionId, payload);
    } else {
      await subs.create(payload);
    }
    showForm.value = false;
    await load();
  } catch (err) {
    error.value = err?.response?.data || err?.message || "Save failed.";
  } finally {
    saving.value = false;
  }
}

async function handleDelete(item) {
  const result = await dialog.confirm({
    title: "Delete Subscription",
    message: `Delete ${item.subscriptionType} subscription for "${item.source}:${item.externalId}"?`,
    variant: "warn",
    buttons: "ok-cancel",
  });
  if (result !== "ok") return;
  try {
    loading.value = true;
    await subs.remove(item.subscriptionId);
    await load();
  } catch (err) {
    error.value = err?.response?.data || err?.message || "Delete failed.";
  } finally {
    loading.value = false;
  }
}

function exportCsv() {
  exportToCsv(
    "IoTSubscriptions",
    [
      { key: "subscriptionType", label: "Type" },
      { key: "source", label: "Source" },
      { key: "externalId", label: "External ID" },
      { key: "enabled", label: "Enabled" },
      { key: "pollMinutes", label: "Poll Minutes" },
      { key: "lastSyncedUtc", label: "Last Synced (UTC)" },
      { key: "lastErrorUtc", label: "Last Error (UTC)" },
      { key: "lastErrorMsg", label: "Last Error Message" },
    ],
    filtered.value,
  );
}

onMounted(load);
</script>

<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card-title class="ww-page-header">
          <div class="ww-page-heading">
            <div class="ww-page-title">IoT Subscriptions</div>
            <div class="ww-page-subtitle">
              External data feeds (counter APIs, weather, satellite) that WayWyser pulls into your IoT dashboards.
            </div>
          </div>
          <div class="d-flex ga-2">
            <v-btn color="success" variant="outlined" @click="exportCsv">
              <v-icon start icon="mdi-file-excel" /> Export to Excel
            </v-btn>
            <v-btn color="primary" @click="openCreate">
              <v-icon start icon="mdi-plus" /> New Subscription
            </v-btn>
          </div>
        </v-card-title>
      </v-col>
    </v-row>

    <v-row style="margin-top: 0">
      <v-col cols="12">
        <v-card elevation="3">
          <v-toolbar flat color="white" dense class="px-4 align-center">
            <v-toolbar-title class="pa-0 ma-0 d-flex align-center">
              <v-text-field
                v-model="search"
                placeholder="Search subscriptions"
                clearable
                prepend-inner-icon="mdi-magnify"
                hide-details
                dense
                density="compact"
                solo
                class="mx-2 pa-0 search-field"
                style="max-width: 300px"
              />
            </v-toolbar-title>
            <v-spacer />
            <v-btn color="primary" @click="load" :loading="loading" prepend-icon="mdi-refresh">
              Refresh
            </v-btn>
          </v-toolbar>

          <v-alert
            v-if="error"
            type="error"
            variant="tonal"
            dismissible
            class="ma-4"
            @update:model-value="error = ''"
          >
            {{ error }}
          </v-alert>

          <v-data-table
            :headers="headers"
            :items="filtered"
            :loading="loading"
            class="elevation-1 iot-subs-table"
            item-key="subscriptionId"
            density="comfortable"
            loading-text="Loading subscriptions…"
            no-data-text="No subscriptions configured."
            hide-default-footer
            :items-per-page="-1"
            @click:row="(_, p) => openEdit(p?.item ?? p)"
          >
            <template #item.subscriptionType="{ item }">
              <v-chip size="small" color="info" variant="elevated" label>
                {{ item.subscriptionType }}
              </v-chip>
            </template>
            <template #item.source="{ item }">
              <span class="font-weight-medium">{{ item.source }}</span>
            </template>
            <template #item.auth="{ item }">
              <v-chip
                size="small"
                :color="item.apiKey ? 'success' : 'grey'"
                variant="flat"
                label
              >
                {{ item.apiKey ? "authenticated" : "public" }}
              </v-chip>
            </template>
            <template #item.enabled="{ item }">
              <v-icon :color="item.enabled ? 'success' : 'grey'">
                {{ item.enabled ? "mdi-check-circle" : "mdi-close-circle-outline" }}
              </v-icon>
            </template>
            <template #item.pollMinutes="{ item }">
              <span>{{ item.pollMinutes ?? "default" }}</span>
            </template>
            <template #item.lastSyncedUtc="{ item }">
              <span>{{ fmtDate(item.lastSyncedUtc) }}</span>
            </template>
            <template #item.status="{ item }">
              <v-tooltip v-if="item.lastErrorMsg" location="top">
                <template #activator="{ props }">
                  <v-chip v-bind="props" size="small" color="error" variant="flat" label>
                    <v-icon start size="small">mdi-alert</v-icon> error
                  </v-chip>
                </template>
                <div style="max-width: 360px">
                  <div><strong>Last error:</strong> {{ fmtDate(item.lastErrorUtc) }}</div>
                  <div>{{ item.lastErrorMsg }}</div>
                </div>
              </v-tooltip>
              <v-chip
                v-else-if="item.lastSyncedUtc"
                size="small"
                color="success"
                variant="flat"
                label
              >
                <v-icon start size="small">mdi-check</v-icon> ok
              </v-chip>
              <v-chip v-else size="small" color="grey" variant="flat" label>
                pending
              </v-chip>
            </template>
            <template #item.actions="{ item }">
              <div class="d-flex align-center" style="gap: 8px">
                <v-btn
                  icon="mdi-pencil"
                  variant="text"
                  size="small"
                  color="primary"
                  @click.stop="openEdit(item)"
                  title="Edit"
                />
                <v-btn
                  icon="mdi-delete"
                  variant="text"
                  size="small"
                  color="error"
                  @click.stop="handleDelete(item)"
                  title="Delete"
                />
              </div>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="showForm" max-width="720" persistent>
      <v-card>
        <v-card-title>
          {{ form.subscriptionId ? "Edit Subscription" : "New Subscription" }}
        </v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-select
                v-model="form.subscriptionType"
                :items="SUBSCRIPTION_TYPES"
                label="Subscription Type *"
                density="comfortable"
                @update:model-value="form.source = availableSources[0] || ''"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-select
                v-model="form.source"
                :items="availableSources"
                label="Source *"
                density="comfortable"
                :hint="availableSources.length === 0 ? 'No vendors registered yet for this type' : ''"
                persistent-hint
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="form.externalId"
                label="External ID *"
                hint="The upstream org id / station id / area code for this source"
                persistent-hint
                density="comfortable"
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="form.apiKey"
                label="API Key"
                :type="showApiKey ? 'text' : 'password'"
                :append-inner-icon="showApiKey ? 'mdi-eye-off' : 'mdi-eye'"
                @click:append-inner="showApiKey = !showApiKey"
                hint="Leave blank for public/unauthenticated sources"
                persistent-hint
                density="comfortable"
              />
            </v-col>
            <v-col cols="12">
              <v-textarea
                v-model="form.config"
                label="Config (JSON)"
                hint='Source-specific options, e.g. {"granularity":"hour","backfillDays":30}'
                persistent-hint
                rows="3"
                density="comfortable"
                auto-grow
              />
            </v-col>
            <v-col cols="6" sm="3">
              <v-switch
                v-model="form.enabled"
                color="success"
                label="Enabled"
                density="comfortable"
                hide-details
              />
            </v-col>
            <v-col cols="6" sm="3">
              <v-text-field
                v-model="form.pollMinutes"
                type="number"
                label="Poll Minutes"
                hint="Override (else system default)"
                persistent-hint
                density="comfortable"
                min="1"
              />
            </v-col>
          </v-row>
          <v-alert v-if="error" type="error" variant="tonal" class="mt-2">
            {{ error }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="showForm = false" :disabled="saving">Cancel</v-btn>
          <v-btn color="primary" @click="save" :loading="saving">
            {{ form.subscriptionId ? "Save Changes" : "Create" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<style scoped>
.iot-subs-table :deep(.v-data-table__tr) {
  cursor: pointer;
}
.iot-subs-table :deep(.v-data-table__tr:hover) {
  background-color: rgba(0, 0, 0, 0.04) !important;
}
</style>
